/**
 * #427 W1a — staff-facing Time Clock card (spec §5 W1a, D2, D5).
 *
 * State machine derived from `GET /me/today` (server truth, D2 — server
 * time only, the client never fabricates punch instants):
 *   clocked_out → single filled-primary "Clock in"
 *   clocked_in  → "Clock out" hero + "Start break" secondary (two-option
 *                 inline choice: Rest 10 min · Meal — no modal-on-modal)
 *   on_break    → "End break" prominent (+ ghost "Clock out", which
 *                 auto-ends the open break server-side)
 *
 * Design Philosophy principles served:
 *   P2 (one primary per view) — the card renders `ferro-btn--primary`
 *     only while clocked out (Clock In is the view's sole primary per
 *     spec W1a) or when the host view has not demoted it via the
 *     `primaryDemoted` prop; hosts lift the status via `onStatusChange`
 *     and demote their own queue primaries while clocked out.
 *   P4 (fast / optimistic) — pressed + disabled state applies
 *     synchronously on tap (<100ms ack); data is NEVER optimistically
 *     mutated — we reconcile against the server response, and on
 *     failure we roll back visibly with the spec's exact error stance.
 *   P7 (built for hands) — ≥44px targets, no hover-only affordances.
 *
 * Five states: default · loading skeleton · empty (teaching copy in the
 * punch list) · error (recovery copy + retry). A "stale" state is not
 * applicable: the card refetches on mount and reconciles after every
 * punch, so it never renders aged data as current.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ClientConfig } from '@ferro/config';
import {
  adminTimeclockApi,
  humanizeApiError,
  type TimeclockBreak,
  type TimeclockEntry,
  type TimeclockMeToday,
} from '../../lib/api.js';
// Sawyer 14 early-clockin-gate (E1): the pending-early chip style.
import './early-clockin.css';

export type TimeClockStatus = 'unknown' | 'clocked_out' | 'clocked_in' | 'on_break';

export interface TimeClockCardProps {
  config: ClientConfig;
  /** When true the card renders its actions as secondary (the host view
   *  owns the filled-primary — e.g. a clocked-in groomer's queue).
   *  Spec W1a: while clocked OUT the card ignores this and renders
   *  Clock In as the view's sole filled-primary. */
  primaryDemoted?: boolean;
  /** Hosts (persona homes) lift the punch status to demote their own
   *  queue primaries while the user is clocked out (spec W1a / P2). */
  onStatusChange?: (status: TimeClockStatus) => void;
}

type PunchAction = 'clock_in' | 'clock_out' | 'break_rest' | 'break_meal' | 'break_end';

interface Notice {
  kind: 'error' | 'info';
  message: string;
  actionLabel?: string;
  onAction?: () => void;
}

/** Spec §5 W1a exact error stance — the punch did NOT happen. */
const PUNCH_NOT_RECORDED =
  'Your punch was NOT recorded — check your connection and try again.';

/** 409 conflict codes (second tab / kiosk raced us): refetch + render
 *  server truth instead of pretending the tap worked (AC 2). */
const CONFLICT_COPY: Record<string, string> = {
  already_clocked_in: 'You were already clocked in — showing the live record.',
  not_clocked_in: 'You were not clocked in — showing the live record.',
  break_already_open: 'A break was already running — showing the live record.',
  no_open_break: 'No break was running — showing the live record.',
};

/** Trusted-device fingerprint (spec D5): a random uuid persisted per
 *  browser, sent with every self-punch. Storage failures (private
 *  browsing) degrade to an un-fingerprinted punch, never a crash. */
const DEVICE_ID_KEY = 'ferro.admin.timeclock.deviceId';

export function getOrCreateDeviceId(): string | undefined {
  try {
    const existing = window.localStorage.getItem(DEVICE_ID_KEY);
    if (existing) return existing;
    const id =
      typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
        ? crypto.randomUUID()
        : // RFC-4122-shaped fallback for non-secure contexts.
          'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
            const r = Math.floor(Math.random() * 16);
            const v = c === 'x' ? r : (r % 4) + 8;
            return v.toString(16);
          });
    window.localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    return undefined;
  }
}

/** Client-owns-local-day contract (wire note in lib/api.ts): the day
 *  window is the BROWSER's local midnight→midnight, sent as UTC ISO
 *  instants. */
export function localDayBoundsUtc(now: Date = new Date()): { fromIso: string; toIso: string } {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
  return { fromIso: start.toISOString(), toIso: end.toISOString() };
}

/** mm:ss under an hour, h:mm beyond (spec W1a). */
export function formatElapsed(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSec / 3600);
  if (hours > 0) {
    const minutes = Math.floor((totalSec % 3600) / 60);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
  }
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
}

function formatClockTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function openBreakOf(entry: TimeclockEntry): TimeclockBreak | undefined {
  return entry.breaks.find((b) => b.end_at === null);
}

function sourceLabel(source: string): string {
  if (source === 'kiosk') return 'Kiosk';
  if (source === 'admin') return 'Manager';
  return 'Self';
}

function chipClass(source: string): string {
  const known = source === 'kiosk' || source === 'admin' || source === 'self';
  return `ferro-punch-chip${known && source !== 'self' ? ` ferro-punch-chip--${source}` : ''}`;
}

export function TimeClockCard({
  config,
  primaryDemoted = false,
  onStatusChange,
}: TimeClockCardProps): JSX.Element {
  const apiUrl = config.deployment.apiUrl;

  const [data, setData] = useState<TimeclockMeToday | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [pendingAction, setPendingAction] = useState<PunchAction | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);
  const [breakChooserOpen, setBreakChooserOpen] = useState(false);
  const [nowTick, setNowTick] = useState(() => Date.now());

  // Punch-list day window is captured per load so the list and the
  // request always agree (fresh bounds on every load call).
  const load = useCallback(
    async (opts: { quiet?: boolean } = {}): Promise<boolean> => {
      if (!opts.quiet) {
        setIsLoading(true);
        setLoadError(null);
      }
      const { fromIso, toIso } = localDayBoundsUtc();
      const res = await adminTimeclockApi.meToday(apiUrl, fromIso, toIso);
      if (!opts.quiet) setIsLoading(false);
      if (res.error !== undefined || !res.data) {
        if (!opts.quiet) setLoadError(res.error ?? 'Unable to load your time clock.');
        return false;
      }
      setData(res.data);
      return true;
    },
    [apiUrl],
  );

  useEffect(() => {
    void load();
  }, [load]);

  const open = data?.open ?? null;
  const openBreak = open ? openBreakOf(open) : undefined;

  const status: TimeClockStatus =
    isLoading || (!data && loadError !== null)
      ? 'unknown'
      : !open
        ? 'clocked_out'
        : openBreak
          ? 'on_break'
          : 'clocked_in';

  // Lift the status so host views can run the W1a primary hierarchy.
  const onStatusChangeRef = useRef(onStatusChange);
  onStatusChangeRef.current = onStatusChange;
  useEffect(() => {
    onStatusChangeRef.current?.(status);
  }, [status]);

  // Live elapsed timer — 1/sec while an entry is open (spec W1a).
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setInterval(() => setNowTick(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [open?.id]); // eslint-disable-line react-hooks/exhaustive-deps -- re-arm per entry, not per render

  const callFor = (
    action: PunchAction,
  ): Promise<{ error?: string; code?: string; data?: unknown }> => {
    const deviceId = getOrCreateDeviceId();
    switch (action) {
      case 'clock_in':
        return adminTimeclockApi.clockIn(apiUrl, deviceId);
      case 'clock_out':
        return adminTimeclockApi.clockOut(apiUrl, deviceId);
      case 'break_rest':
        return adminTimeclockApi.startBreak(apiUrl, 'rest');
      case 'break_meal':
        return adminTimeclockApi.startBreak(apiUrl, 'meal');
      case 'break_end':
        return adminTimeclockApi.endBreak(apiUrl);
    }
  };

  const perform = async (action: PunchAction): Promise<void> => {
    // P4: pressed + disabled applies synchronously — sub-100ms ack.
    setPendingAction(action);
    setNotice(null);
    setBreakChooserOpen(false);
    const res = await callFor(action);
    if (res.error !== undefined) {
      // #711 — `error` is sanitized human copy now; the machine token for
      // typed branching lives on `code` (fallback keeps older wire shapes
      // working).
      const conflictCopy = CONFLICT_COPY[res.code ?? res.error];
      if (conflictCopy !== undefined) {
        // Another tab / the kiosk already changed the state: render
        // server truth, never a stale pretend-success (AC 2).
        await load({ quiet: true });
        setNotice({ kind: 'info', message: conflictCopy });
      } else {
        // Visible rollback (P4/P5): no data was mutated optimistically,
        // so rollback = release the pressed state + say plainly that
        // the punch did not happen and how to recover.
        setNotice({
          kind: 'error',
          message: PUNCH_NOT_RECORDED,
          actionLabel: 'Retry',
          onAction: () => void perform(action),
        });
      }
      setPendingAction(null);
      return;
    }
    // Success: reconcile against server truth (single quiet refetch).
    const refreshed = await load({ quiet: true });
    // Sawyer 14 (E1): an early clock-in says so plainly. Absent on a server
    // without the lane, so the card behaves exactly as before (AC21).
    const earlyNotice =
      action === 'clock_in' &&
      typeof (res.data as { early_notice?: unknown } | undefined)?.early_notice === 'string'
        ? (res.data as { early_notice: string }).early_notice
        : null;
    if (refreshed && earlyNotice) {
      setNotice({ kind: 'info', message: earlyNotice });
    }
    if (!refreshed) {
      // The punch DID record — never show the not-recorded copy here.
      setNotice({
        kind: 'info',
        message: 'Your punch was recorded, but the latest view did not load.',
        actionLabel: 'Refresh',
        onAction: () => void load({ quiet: true }),
      });
    }
    setPendingAction(null);
  };

  const busy = pendingAction !== null;

  // Spec W1a: clocked-out ⇒ Clock In is the view's sole filled-primary
  // regardless of the host's demotion; otherwise the host decides.
  const heroIsPrimary = status === 'clocked_out' || !primaryDemoted;
  const heroClass = (action: PunchAction): string =>
    [
      'ferro-btn',
      'ferro-timeclock-hero-btn',
      heroIsPrimary ? 'ferro-btn--primary' : 'ferro-btn--deprioritized',
      pendingAction === action ? 'is-pending' : '',
    ]
      .filter(Boolean)
      .join(' ');

  const entries = [...(data?.entries ?? [])].sort(
    (a, b) => Date.parse(a.clock_in_at) - Date.parse(b.clock_in_at),
  );

  const statusPill = (): JSX.Element | null => {
    if (status === 'clocked_in')
      return <span className="ferro-status-pill ferro-status-pill--success">Clocked in</span>;
    if (status === 'on_break')
      return <span className="ferro-status-pill ferro-status-pill--pending">On break</span>;
    if (status === 'clocked_out')
      return <span className="ferro-status-pill ferro-status-pill--neutral">Clocked out</span>;
    return null;
  };

  return (
    <section className="ferro-card ferro-timeclock-card" data-testid="timeclock-card">
      <div className="ferro-timeclock-card__head">
        <h2 className="ferro-timeclock-card__title">Time clock</h2>
        {statusPill()}
      </div>

      {/* Loading skeleton (never a blank card — P4). Matches the house
          ferro-skeleton idiom used by the persona homes. */}
      {isLoading && (
        <div className="ferro-skeleton-stack" data-testid="timeclock-skeleton">
          <div className="ferro-skeleton ferro-skeleton--title" />
          <div className="ferro-skeleton ferro-skeleton--stat" />
          <div className="ferro-skeleton ferro-skeleton--text" />
        </div>
      )}

      {/* Load error — situation + recovery, never the technology. */}
      {!isLoading && !data && loadError !== null && (
        <div
          className="ferro-alert ferro-alert--error ferro-alert--dismissible"
          role="alert"
          data-testid="timeclock-error"
        >
          <span>{humanizeApiError(loadError)}</span>
          <button
            type="button"
            className="ferro-btn ferro-btn--sm ferro-btn--outline"
            onClick={() => void load()}
          >
            Retry
          </button>
        </div>
      )}

      {!isLoading && data && (
        <>
          {open ? (
            <div className="ferro-timeclock-card__now">
              <p className="ferro-timeclock-card__since">
                Clocked in since {formatClockTime(open.clock_in_at)}
              </p>
              <p className="ferro-timeclock-card__timer" data-testid="timeclock-timer">
                {formatElapsed(nowTick - Date.parse(open.clock_in_at))}
              </p>
              {open.early?.status === 'pending' ? (
                /* Sawyer 14 (E1): no pay-policy wording to staff (refuter
                   C14); the manager sees "Paid from" on Punch records. */
                <p className="ferro-timeclock-card__early" data-testid="timeclock-early-pending">
                  <span className="ferro-tc-chip ferro-tc-chip--early-pending">
                    Early start waiting for a manager
                  </span>
                </p>
              ) : null}
              {openBreak && (
                <p className="ferro-timeclock-card__break-note">
                  On {openBreak.kind === 'meal' ? 'meal' : 'rest'} break since{' '}
                  {formatClockTime(openBreak.start_at)}
                </p>
              )}
            </div>
          ) : (
            <p className="ferro-timeclock-card__since">
              You are clocked out{entries.length > 0 ? ' — nice work today' : ''}.
            </p>
          )}

          {notice && (
            <div
              className={`ferro-alert ferro-alert--${notice.kind} ferro-alert--dismissible`}
              role={notice.kind === 'error' ? 'alert' : 'status'}
              data-testid="timeclock-punch-notice"
            >
              <span>{notice.message}</span>
              {notice.onAction ? (
                <button
                  type="button"
                  className="ferro-btn ferro-btn--sm ferro-btn--outline"
                  disabled={busy}
                  onClick={notice.onAction}
                >
                  {notice.actionLabel}
                </button>
              ) : (
                <button
                  type="button"
                  className="ferro-alert__dismiss"
                  onClick={() => setNotice(null)}
                >
                  Dismiss
                </button>
              )}
            </div>
          )}

          <div className="ferro-timeclock-card__actions">
            {status === 'clocked_out' && (
              <button
                type="button"
                className={heroClass('clock_in')}
                data-testid="timeclock-clock-in"
                disabled={busy}
                aria-busy={pendingAction === 'clock_in'}
                onClick={() => void perform('clock_in')}
              >
                Clock in
              </button>
            )}

            {status === 'clocked_in' && (
              <>
                <button
                  type="button"
                  className={heroClass('clock_out')}
                  data-testid="timeclock-clock-out"
                  disabled={busy}
                  aria-busy={pendingAction === 'clock_out'}
                  onClick={() => void perform('clock_out')}
                >
                  Clock out
                </button>
                {/* Two-option inline choice — no modal-on-modal (P2). */}
                {!breakChooserOpen ? (
                  <button
                    type="button"
                    className="ferro-btn ferro-btn--outline"
                    data-testid="timeclock-break-start"
                    disabled={busy}
                    onClick={() => setBreakChooserOpen(true)}
                  >
                    Start break
                  </button>
                ) : (
                  <div
                    className="ferro-timeclock-break-choice"
                    role="group"
                    aria-label="Choose a break type"
                  >
                    <button
                      type="button"
                      className={`ferro-btn ferro-btn--outline${pendingAction === 'break_rest' ? ' is-pending' : ''}`}
                      data-testid="timeclock-break-rest"
                      disabled={busy}
                      onClick={() => void perform('break_rest')}
                    >
                      Rest · 10 min
                    </button>
                    <button
                      type="button"
                      className={`ferro-btn ferro-btn--outline${pendingAction === 'break_meal' ? ' is-pending' : ''}`}
                      data-testid="timeclock-break-meal"
                      disabled={busy}
                      onClick={() => void perform('break_meal')}
                    >
                      Meal
                    </button>
                    <button
                      type="button"
                      className="ferro-btn ferro-btn--ghost"
                      disabled={busy}
                      onClick={() => setBreakChooserOpen(false)}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </>
            )}

            {status === 'on_break' && (
              <>
                <button
                  type="button"
                  className={heroClass('break_end')}
                  data-testid="timeclock-break-end"
                  disabled={busy}
                  aria-busy={pendingAction === 'break_end'}
                  onClick={() => void perform('break_end')}
                >
                  End break
                </button>
                {/* Clocking out auto-ends the open break server-side —
                    leaving at end of day is never a dead end (P2). */}
                <button
                  type="button"
                  className={`ferro-btn ferro-btn--ghost${pendingAction === 'clock_out' ? ' is-pending' : ''}`}
                  data-testid="timeclock-clock-out"
                  disabled={busy}
                  onClick={() => void perform('clock_out')}
                >
                  Clock out
                </button>
              </>
            )}
          </div>

          {/* Today's punches — in/out times, source chips, break lines. */}
          <div className="ferro-timeclock-punches" data-testid="timeclock-punches">
            <h3 className="ferro-timeclock-punches__title">Today</h3>
            {entries.length === 0 ? (
              // Empty teaching state: what this is + what to do first.
              <p className="ferro-timeclock-empty" data-testid="timeclock-empty">
                No punches yet today. Tap Clock in when you start work — your
                hours count from that first tap.
              </p>
            ) : (
              entries.map((entry) => (
                <div className="ferro-punch-row" key={entry.id}>
                  <div className="ferro-punch-row__main">
                    <span className="ferro-punch-row__times">
                      {formatClockTime(entry.clock_in_at)} –{' '}
                      {entry.clock_out_at ? formatClockTime(entry.clock_out_at) : 'now'}
                    </span>
                    <span className={chipClass(entry.clock_in_source)}>
                      {sourceLabel(entry.clock_in_source)}
                    </span>
                    {entry.clock_out_source !== null &&
                      entry.clock_out_source !== entry.clock_in_source && (
                        <span className={chipClass(entry.clock_out_source)}>
                          out: {sourceLabel(entry.clock_out_source)}
                        </span>
                      )}
                  </div>
                  {entry.breaks.map((b) => (
                    <p className="ferro-punch-break" key={b.id}>
                      {b.kind === 'meal' ? 'Meal' : 'Rest'} break ·{' '}
                      {formatClockTime(b.start_at)} –{' '}
                      {b.end_at ? formatClockTime(b.end_at) : 'now'}
                      {b.paid ? '' : ' · unpaid'}
                    </p>
                  ))}
                </div>
              ))
            )}
          </div>
        </>
      )}
    </section>
  );
}
